'use client'

import Button from '@components/primitives/Button'
import { Icon } from '@components/primitives/Icon'
import Link from '@components/primitives/Link'
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft'
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight'
import urlWithQueryParams from '@lib/routes/helpers/urlWithQueryParams'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { generatePaginationItems } from '@modules/trials/search/generatePaginationItems'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'

export default function SearchResultsPagination({
  className = '',
  currentPage,
  totalPages,
}: {
  className?: string
  currentPage: number
  totalPages: number
}) {
  const router = useRouter()
  const pathname = usePathname()

  const searchParams = new URLSearchParams(useSearchParams() ?? '')

  const handlePreviousClick = () => {
    trackEvent(TrackingEvents.SEARCH_LANDER_PAGINATION_CHANGED, {
      val: `prev-${currentPage - 1}`,
    })
    router.push(
      urlWithQueryParams(pathname ?? '', {
        ...Object.fromEntries(searchParams),
        page: (currentPage - 1).toString(),
      }),
    )
  }

  const handleNextClick = () => {
    trackEvent(TrackingEvents.SEARCH_LANDER_PAGINATION_CHANGED, {
      val: `next-${currentPage + 1}`,
    })
    router.push(
      urlWithQueryParams(pathname ?? '', {
        ...Object.fromEntries(searchParams),
        page: (currentPage + 1).toString(),
      }),
    )
  }

  // Generate pagination items (pages and ellipses)
  const paginationItems = generatePaginationItems(currentPage, totalPages)

  return (
    <div className={`flex justify-center gap-1 sm:gap-2 ${className}`}>
      <Button
        className='items-center justify-center self-center rounded border-[1.5px] border-neutral200 bg-white p-2 text-center text-primary600 shadow sm:px-4'
        disabled={currentPage === 1}
        onClick={handlePreviousClick}
      >
        <Icon icon={faChevronLeft} />
      </Button>

      {paginationItems.map((item, index) =>
        item.type === 'ellipsis' ? (
          <span
            className='items-center justify-center self-center text-neutral800 sm:px-2'
            key={`ellipsis-${index}`}
          >
            ...
          </span>
        ) : (
          <Link
            className={`items-center justify-center self-center rounded border-[1.5px] border-neutral200 bg-white px-4 py-2 text-center text-neutral800 shadow-sm ${
              item.value === currentPage
                ? 'border-primary500 bg-primary100 text-neutral800'
                : ''
            }`}
            href={urlWithQueryParams(pathname ?? '', {
              ...Object.fromEntries(searchParams),
              page: item.value.toString(),
            })}
            key={index}
          >
            {item.value}
          </Link>
        ),
      )}

      <Button
        className='items-center justify-center self-center rounded border-[1.5px] border-neutral200 bg-white p-2 text-center text-primary600 shadow sm:px-4'
        disabled={currentPage === totalPages}
        onClick={handleNextClick}
      >
        <Icon icon={faChevronRight} />
      </Button>
    </div>
  )
}
